import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { map, tap, catchError, switchMap } from 'rxjs/operators';
import { BehaviorSubject, Observable, of, throwError } from "rxjs";
import { environment } from '../../../environments/environment';
import { CandidatoDatosPersonalesInterface } from "../interfaces/candidato/candidato-datos-personales.interface";
import { CandidatoLoginResponseInterface } from "../interfaces/candidato/candidato-login-response.interface";
import { EncryptionService } from './encryption.service';


@Injectable({
    providedIn: 'root'
})
export class AuthApiService {

    private tokenKey = 'authToken';
    private isAuthenticated = new BehaviorSubject<boolean>(this.hasToken());
    private currentUser = new BehaviorSubject<CandidatoLoginResponseInterface | null>(this.getStoredUser())
    
    constructor(private http: HttpClient, private encryptionService: EncryptionService) { }
   
    /**
     * Método para obtener candidato usando get-candidate (servicio alterno)
     */
    getCandidate(email: string, password: string): Observable<any> {
        // URL del endpoint - usar URL completa si no hay proxy, sino usar proxy
        const url = environment.useProxy 
            ? `${environment.urlAppian}/get-candidate`
            : (environment.urlAppianGetCandidate || `${environment.urlAppian}/get-candidate`);
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'appian-api-key': environment.appianApiKey,
            'Accept': 'application/json'
        });
        const body = {
            correo: email,
            pas: password
        };

        console.log('🔍 Llamando a get-candidate con:', { email, password: '***' });

        return this.http.post(url, body, { headers, responseType: 'text' }).pipe(
            map((res: any) => {
                console.log('📥 Respuesta completa de get-candidate (texto):', res);
                
                if (!res || res.trim() === '') {
                    console.warn('⚠️ Respuesta vacía de get-candidate');
                    return { error: true, message: 'Sin datos', candidato: 'Sin datos' };
                }

                // Intentar parsear como JSON
                try {
                    const parsed = JSON.parse(res);
                    console.log('✅ Respuesta parseada como JSON:', parsed);
                    return parsed;
                } catch (e) {
                    console.warn('⚠️ No es JSON válido, procesando como texto...');
                    
                    // Intentar extraer JSON del texto si está embebido
                    const jsonMatch = res.match(/\{[\s\S]*\}/);
                    if (jsonMatch) {
                        try {
                            const parsed = JSON.parse(jsonMatch[0]);
                            console.log('✅ JSON extraído y parseado del texto:', parsed);
                            return parsed;
                        } catch (e2) {
                            console.warn('⚠️ No se pudo parsear el JSON extraído');
                        }
                    }
                    
                    // Si la respuesta contiene "Sin datos", retornar error
                    if (res.toLowerCase().includes('sin datos')) {
                        return { error: true, message: 'Sin datos', candidato: 'Sin datos' };
                    }
                    
                    return { error: true, message: res, candidato: 'Sin datos' };
                }
            }),
            catchError(err => {
                console.error('❌ get-candidate error:', err);
                return of({ error: true, message: 'Error al validar credenciales', candidato: 'Sin datos' });
            })
        );
    }

    login2(email: string, password?: string): Observable<any> {
        // Paso 1: Intentar obtener resume con obtener-resume
        // Si useProxy es true y urlAppian es relativa, usar proxy, sino usar URL completa
        const url = (environment.useProxy && environment.urlAppian.startsWith('/')) 
            ? `${environment.urlAppian}/obtener-resume`
            : environment.urlAppianObtenerResume;
        const headers = new HttpHeaders({
            'appian-api-key': environment.appianApiKey,
            'Accept': 'application/json'
        });
        const params = new HttpParams().set('correo', email);

        console.log('🔍 Paso 1: Llamando a obtener-resume con email:', email);

        return this.http.get(url, { headers, params, responseType: 'text' }).pipe(
            map((res: any) => {
                // Manejar caso cuando res es null directamente
                if (res === null || res === undefined) {
                    console.warn('⚠️ Respuesta null o undefined de obtener-resume');
                    return { error: true, message: 'resume no encontrado', resumeNotFound: true };
                }
                
                // Convertir a string si es necesario
                const resString = typeof res === 'string' ? res : JSON.stringify(res);
                console.log('📥 Respuesta completa de obtener-resume (texto):', resString);
                
                if (!resString || resString === 'null' || resString.trim() === '' || resString.trim() === 'null') {
                    console.warn('⚠️ Respuesta vacía o null');
                    return { error: true, message: 'resume no encontrado', resumeNotFound: true };
                }

                // Si ya es un objeto, verificar si está vacío
                if (typeof res === 'object' && res !== null) {
                    const isEmptyObj = Object.keys(res).length === 0;
                    const isEmptyArr = Array.isArray(res) && res.length === 0;
                    if (isEmptyObj || isEmptyArr) {
                        console.warn('⚠️ Objeto vacío');
                        return { error: true, message: 'resume no encontrado', resumeNotFound: true };
                    }
                    // Si es un array, tomar el primer elemento
                    if (Array.isArray(res) && res.length > 0) {
                        return res[0];
                    }
                    return res;
                }

                // Detectar si la respuesta indica que no se encontró el resume
                if (resString.toLowerCase().includes('resume no encontrado') || 
                    resString.toLowerCase().includes('no encontrado') ||
                    resString.toLowerCase().includes('not found')) {
                    console.warn('⚠️ Resume no encontrado en obtener-resume');
                    return { error: true, message: 'resume no encontrado', resumeNotFound: true };
                }

                // Intentar parsear como JSON
                try {
                    const parsed = JSON.parse(resString);
                    console.log('✅ Respuesta parseada como JSON:', parsed);
                    
                    // Validar que parsed no sea null
                    if (parsed === null || parsed === undefined) {
                        console.warn('⚠️ JSON parseado es null');
                        return { error: true, message: 'resume no encontrado', resumeNotFound: true };
                    }
                    
                    const isEmptyObj = parsed && typeof parsed === 'object' && Object.keys(parsed).length === 0;
                    const isEmptyArr = Array.isArray(parsed) && parsed.length === 0;
                    if (isEmptyObj || isEmptyArr) {
                        console.warn('⚠️ JSON vacío');
                        return { error: true, message: 'resume no encontrado', resumeNotFound: true };
                    }
                    
                    // Si es un array, tomar el primer elemento (CV más reciente)
                    if (Array.isArray(parsed) && parsed.length > 0) {
                        return parsed[0]; // Retorna el primer CV del array
                    }
                    
                    return parsed; // Si no es array, retorna tal como viene
                } catch (e) {
                    console.warn('⚠️ No es JSON válido, intentando extraer información del texto...');
                    
                    // Intentar extraer JSON del texto si está embebido
                    const jsonMatch = resString.match(/\{[\s\S]*\}/);
                    if (jsonMatch) {
                        try {
                            const parsed = JSON.parse(jsonMatch[0]);
                            console.log('✅ JSON extraído y parseado del texto:', parsed);
                            const isEmptyObj = parsed && typeof parsed === 'object' && Object.keys(parsed).length === 0;
                            const isEmptyArr = Array.isArray(parsed) && parsed.length === 0;
                            if (!isEmptyObj && !isEmptyArr) {
                                const result = Array.isArray(parsed) && parsed.length > 0 ? parsed[0] : parsed;
                                return result;
                            }
                        } catch (e2) {
                            console.warn('⚠️ No se pudo parsear el JSON extraído');
                        }
                    }
                    
                    // Si no se pudo parsear y contiene "no encontrado", retornar error
                    return { error: true, message: 'resume no encontrado', resumeNotFound: true };
                }
            }),
            // Si obtener-resume no encuentra el resume, intentar con get-candidate
            switchMap((result: any) => {
                // Validar que result no sea null
                if (!result || result === null) {
                    console.warn('⚠️ Result es null en switchMap');
                    // Si hay password, intentar con get-candidate
                    if (password) {
                        console.log('🔄 Result null, intentando con get-candidate...');
                        return this.getCandidate(email, password).pipe(
                            map((candidateResult: any) => {
                                if (candidateResult?.candidato && 
                                    candidateResult.candidato !== 'Sin datos' && 
                                    typeof candidateResult.candidato === 'object') {
                                    console.log('✅ Candidato encontrado en get-candidate:', candidateResult.candidato);
                                    return candidateResult.candidato;
                                }
                                return { 
                                    error: true, 
                                    message: 'No pudimos iniciar tu sesión. Verifica que tu usuario y contraseña sean correctos. Si el problema persiste, ponte en contacto con nosotros.',
                                    invalidCredentials: true 
                                };
                            })
                        );
                    }
                    return of({ 
                        error: true, 
                        message: 'no se encontro el usuario',
                        resumeNotFound: true 
                    });
                }
                
                // Si hay datos válidos, retornar directamente
                if (!result.error && !result.resumeNotFound) {
                    console.log('✅ Resume encontrado, continuando con flujo normal');
                    return of(result);
                }
                
                // Si no se encontró el resume y tenemos password, intentar con get-candidate
                if (result.resumeNotFound && password) {
                    console.log('🔄 Resume no encontrado, intentando con get-candidate...');
                    return this.getCandidate(email, password).pipe(
                        map((candidateResult: any) => {
                            // Si get-candidate devuelve candidato con datos válidos
                            if (candidateResult?.candidato && 
                                candidateResult.candidato !== 'Sin datos' && 
                                typeof candidateResult.candidato === 'object') {
                                console.log('✅ Candidato encontrado en get-candidate:', candidateResult.candidato);
                                return candidateResult.candidato;
                            }
                            
                            // Si get-candidate devuelve "Sin datos"
                            if (candidateResult?.candidato === 'Sin datos' || 
                                candidateResult?.error) {
                                console.warn('⚠️ Candidato sin datos en get-candidate');
                                return { 
                                    error: true, 
                                    message: 'No pudimos iniciar tu sesión. Verifica que tu usuario y contraseña sean correctos. Si el problema persiste, ponte en contacto con nosotros.',
                                    invalidCredentials: true 
                                };
                            }
                            
                            return candidateResult;
                        })
                    );
                }
                
                // Si no hay password y no se encontró resume, retornar error
                if (result?.resumeNotFound && !password) {
                    console.warn('⚠️ Resume no encontrado y no hay password para get-candidate');
                    return of({ 
                        error: true, 
                        message: 'no se encontro el usuario',
                        resumeNotFound: true 
                    });
                }
                
                // Otro caso de error
                return of(result);
            }),
            tap((result) => {
                // Validar que result no sea null antes de acceder a sus propiedades
                if (!result || result === null) {
                    console.warn('⚠️ Result es null en tap, reseteando estado');
                    this.isAuthenticated.next(false);
                    this.currentUser.next(null);
                    localStorage.removeItem('authUser');
                    return;
                }
                
                if (!result.error && !result.invalidCredentials && result.resumeNotFound !== true) {
                    // Normalizar respuesta de Appian al formato esperado por el sistema
                    const normalizedUserData: CandidatoLoginResponseInterface = {
                        // Mapear campos del CV de Appian al formato de la interfaz
                        nombre: result.name || result.nombre || '',
                        apellido_pat: result.lastName?.split(' ')[0] || result.apellido1 || '',
                        apellido_mat: result.lastName?.split(' ')[1] || result.apellido2 || '',
                        uid: result.candidatoId?.toString() || result.id?.toString() || '',
                        email: result.email || result.correo || '',
                        preferencias: {
                            puesto: result.hasWorkExperience?.[0]?.position || '',
                            sueldo: result.desiredMonthlySalary || null
                        }
                    };

                    localStorage.setItem('authUser', JSON.stringify(normalizedUserData));
                    this.isAuthenticated.next(true);
                    this.currentUser.next(normalizedUserData);
                } else {
                    // Resetea estado si no se encontró o credenciales inválidas
                    this.isAuthenticated.next(false);
                    this.currentUser.next(null);
                    localStorage.removeItem('authUser');
                }
            }),
            catchError(err => {
                console.error('❌ Error en obtener-resume:', err);
                // Si hay password, intentar con get-candidate como alternativa
                if (password) {
                    console.log('🔄 Error en obtener-resume, intentando con get-candidate...');
                    return this.getCandidate(email, password).pipe(
                        map((candidateResult: any) => {
                            if (candidateResult?.candidato && 
                                candidateResult.candidato !== 'Sin datos' && 
                                typeof candidateResult.candidato === 'object') {
                                console.log('✅ Candidato encontrado en get-candidate:', candidateResult.candidato);
                                return candidateResult.candidato;
                            }
                            return { 
                                error: true, 
                                message: 'No pudimos iniciar tu sesión. Verifica que tu usuario y contraseña sean correctos. Si el problema persiste, ponte en contacto con nosotros.',
                                invalidCredentials: true 
                            };
                        }),
                        catchError(getCandidateErr => {
                            console.error('❌ Error también en get-candidate:', getCandidateErr);
                            return of({ 
                                error: true, 
                                message: 'No pudimos iniciar tu sesión. Verifica que tu usuario y contraseña sean correctos. Si el problema persiste, ponte en contacto con nosotros.' 
                            });
                        })
                    );
                }
                // Si no hay password, retornar error
                return of({ 
                    error: true, 
                    message: 'No pudimos iniciar tu sesión. Verifica que tu usuario y contraseña sean correctos. Si el problema persiste, ponte en contacto con nosotros.' 
                });
            })
        );
    }
    recuperaCandidatoId(email: string): Observable<any> {
        const url = `${environment.urlAppian}/obtener-id`;
        const headers = new HttpHeaders({
            'appian-api-key': environment.appianApiKey,
            'Accept': 'application/json'
        });
        const params = new HttpParams().set('correo', email);

        return this.http.get<any>(url, { headers, params }).pipe(
            map(response => {
                let id = Array.isArray(response) ? response[0] : null
                console.log('✅ Respuesta recuperaCandidatoId:', id)
                return id ;
            }),
            catchError(error => {
                console.error('❌ Error en recuperaCandidatoId:', error);
                return of({ error: true, message: 'no se encontro el usuario' });
            })
        );
    }
    /*login2Respaldo(email: string, password: string): Observable<any> {
        let _data = {
            "username": email, 
            "password": this.encryptionService.encryptToBase64(password)
        };
      return this.http.post<{ token: any, data: any }>(`${environment.urlApiCandidato}/login`, _data).pipe(
        tap(response => {
          localStorage.setItem(this.tokenKey, response.token.value);
          localStorage.setItem('authUser', JSON.stringify(response.data));
          this.isAuthenticated.next(true);
          this.currentUser.next(response.data);
        })
      );
    }*/

    logout(): void {
        localStorage.removeItem(this.tokenKey);
        localStorage.removeItem('authUser');
        localStorage.removeItem('candidatoId');
        localStorage.removeItem('proceso_id');
        localStorage.removeItem('jobSelected');
        this.isAuthenticated.next(false);
        this.currentUser.next(null);
        window.location.href = '/empleos/';
    }

    isLoggedIn(): Observable<boolean> {
        // Verificar tanto el estado del BehaviorSubject como el localStorage
        const hasStoredSession = this.hasToken();
        
        // Si hay datos en localStorage pero el BehaviorSubject es false, corregir
        if (hasStoredSession && !this.isAuthenticated.value) {
            this.isAuthenticated.next(true);
        }
        
        return this.isAuthenticated.asObservable();
    }

    getToken(): string | null {
        return localStorage.getItem(this.tokenKey);
    }

    private hasToken(): boolean {
        // Para el flujo de Appian, verificar si existe authUser
        const hasAuthUser = !!localStorage.getItem('authUser');
        // Para el flujo tradicional, verificar si existe authToken
        const hasAuthToken = !!localStorage.getItem(this.tokenKey);
        
        // Retorna true si tiene cualquiera de los dos
        return hasAuthToken || hasAuthUser;
    }

    private getStoredUser(): CandidatoLoginResponseInterface | null {
        const userData = localStorage.getItem('authUser');
        return userData ? JSON.parse(userData) : null;
    }
    getUser(): Observable<CandidatoLoginResponseInterface | null> {
        return this.currentUser.asObservable();
    }
}